// KycDashboardPage.js — Main KYC & Onboarding dashboard
// Entry point rendered at /kyc/* with sub-route tab handling

import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Shield, FileText, Users, AlertTriangle, CheckCircle, Clock, Search, Filter,
  Plus, ChevronRight, Eye, XCircle, BarChart3, Globe, Building2, User,
  ArrowRight, RefreshCw, ChevronDown, Calendar, SortAsc, SortDesc,
  AlertCircle, CheckCircle2, X, ExternalLink, Download, Layers,
} from 'lucide-react';
import useKycMockData from '../../hooks/useKycMockData';
import KycStatusBadge from '../../components/kyc/KycStatusBadge';
import KycRiskBadge from '../../components/kyc/KycRiskBadge';
import { CASE_TYPE_LABELS } from '../../lib/kyc/constants';

// ============================================
// HELPER COMPONENTS
// ============================================

const StatCard = ({ title, value, icon: Icon, color, subtitle, onClick }) => {
  const colors = {
    blue:   'bg-blue-100 text-blue-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600',
    red:    'bg-red-100 text-red-600',
    green:  'bg-green-100 text-green-600',
    amber:  'bg-amber-100 text-amber-600',
    indigo: 'bg-indigo-100 text-indigo-600',
  };
  return (
    <div
      className={`bg-white rounded-xl shadow-sm p-5 border border-gray-100 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colors[color] || colors.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

const CaseTypeBadge = ({ type }) => {
  const typeColors = {
    individual:    'bg-sky-100 text-sky-700',
    corporate:     'bg-violet-100 text-violet-700',
    trust:         'bg-emerald-100 text-emerald-700',
    fund:          'bg-indigo-100 text-indigo-700',
    family_office: 'bg-amber-100 text-amber-700',
    spe:           'bg-rose-100 text-rose-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${typeColors[type] || 'bg-gray-100 text-gray-600'}`}>
      {CASE_TYPE_LABELS[type] || type}
    </span>
  );
};

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-700 mb-1">{title}</h3>
    <p className="text-sm text-gray-500 max-w-md">{description}</p>
  </div>
);

// ============================================
// TAB ROUTING
// ============================================
const TABS = [
  { key: 'queue',      label: 'Case Queue',        icon: Layers,       suffix: '' },
  { key: 'screening',  label: 'Screening Centre',  icon: Shield,       suffix: '/screening' },
  { key: 'reviews',    label: 'Reviews',            icon: Clock,        suffix: '/reviews' },
  { key: 'markets',    label: 'Market Rules',       icon: Globe,        suffix: '/market-rules' },
  { key: 'reporting',  label: 'Reporting',          icon: BarChart3,    suffix: '/reporting' },
];

function getActiveTab(pathname) {
  if (pathname.includes('/screening')) return 'screening';
  if (pathname.includes('/reviews'))   return 'reviews';
  if (pathname.includes('/markets'))   return 'markets';
  if (pathname.includes('/reporting')) return 'reporting';
  return 'queue';
}

// ============================================
// CASE QUEUE TAB
// ============================================
const CaseQueueTab = ({ cases, navigate, kycBase }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const filteredCases = useMemo(() => {
    let result = [...cases];

    if (statusFilter !== 'all') result = result.filter(c => c.status === statusFilter);
    if (riskFilter !== 'all') result = result.filter(c => c.risk_rating === riskFilter);
    if (typeFilter !== 'all') result = result.filter(c => c.case_type === typeFilter);
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.case_ref.toLowerCase().includes(term) ||
        c.investor_name.toLowerCase().includes(term) ||
        (c.assigned_analyst && c.assigned_analyst.toLowerCase().includes(term))
      );
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':   return new Date(a.created_at) - new Date(b.created_at);
        case 'due_date':
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return new Date(a.due_date) - new Date(b.due_date);
        case 'risk':
          const riskOrder = { very_high: 0, high: 1, medium: 2, low: 3 };
          return (riskOrder[a.risk_rating] ?? 4) - (riskOrder[b.risk_rating] ?? 4);
        case 'newest':
        default:         return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    return result;
  }, [cases, statusFilter, riskFilter, typeFilter, searchTerm, sortBy]);

  const getDaysRemaining = (dueDate, status) => {
    if (!dueDate || ['approved', 'rejected', 'closed', 'expired'].includes(status)) return null;
    const days = Math.ceil((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <div>
      {/* Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by case ref, investor, analyst..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="in_review">In Review</option>
            <option value="screening">Screening</option>
            <option value="risk_assessment">Risk Assessment</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="information_requested">Info Requested</option>
          </select>

          {/* Risk Filter */}
          <select
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="all">All Risk Levels</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="very_high">Very High</option>
          </select>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="all">All Types</option>
            <option value="individual">Individual</option>
            <option value="corporate">Corporate</option>
            <option value="trust">Trust</option>
            <option value="fund">Fund</option>
            <option value="family_office">Family Office</option>
            <option value="spe">SPE</option>
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="due_date">Due Date</option>
            <option value="risk">Risk Level</option>
          </select>

          <button
            onClick={() => navigate(`${kycBase}/cases/new`)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Case
          </button>
        </div>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-3 px-1">
        <p className="text-sm text-gray-500">{filteredCases.length} case{filteredCases.length !== 1 ? 's' : ''} found</p>
      </div>

      {/* Case List */}
      {filteredCases.length === 0 ? (
        <EmptyState icon={FileText} title="No cases found" description="Try adjusting your filters or create a new case." />
      ) : (
        <div className="space-y-2">
          {filteredCases.map((c) => {
            const days = getDaysRemaining(c.due_date, c.status);
            return (
              <div
                key={c.id}
                onClick={() => navigate(`${kycBase}/cases/${c.id}`)}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md hover:border-gray-200 transition-all cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  {/* Case Ref & Type */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900">{c.case_ref}</span>
                      <CaseTypeBadge type={c.case_type} />
                    </div>
                    <p className="text-sm text-gray-600 truncate">{c.investor_name}</p>
                  </div>

                  {/* Status */}
                  <div className="flex-shrink-0">
                    <KycStatusBadge status={c.status} />
                  </div>

                  {/* Risk */}
                  <div className="flex-shrink-0 w-20 text-center">
                    {c.risk_rating ? (
                      <KycRiskBadge rating={c.risk_rating} />
                    ) : (
                      <span className="text-xs text-gray-400">--</span>
                    )}
                  </div>

                  {/* Analyst */}
                  <div className="flex-shrink-0 w-32 hidden md:block">
                    <div className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-600 truncate">{c.assigned_analyst || 'Unassigned'}</span>
                    </div>
                  </div>

                  {/* SLA / Due Date */}
                  <div className="flex-shrink-0 w-28 hidden lg:block text-right">
                    {days !== null ? (
                      <div className={`text-xs font-medium ${days < 0 ? 'text-red-600' : days <= 3 ? 'text-amber-600' : 'text-gray-500'}`}>
                        {days < 0 ? (
                          <span className="flex items-center justify-end gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {Math.abs(days)}d overdue
                          </span>
                        ) : (
                          <span className="flex items-center justify-end gap-1">
                            <Clock className="w-3 h-3" />
                            {days}d remaining
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">--</span>
                    )}
                    {c.due_date && (
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        Due {new Date(c.due_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  {/* Arrow */}
                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================
// SCREENING CENTRE TAB
// ============================================
const ScreeningCentreTab = ({ screeningHits, navigate, kycBase }) => {
  const [selected, setSelected] = useState([]);
  const pendingHits = screeningHits.filter(h => h.status === 'pending_review');

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selected.length === pendingHits.length) {
      setSelected([]);
    } else {
      setSelected(pendingHits.map(h => h.id));
    }
  };

  const hitTypeColors = {
    pep:           'bg-orange-100 text-orange-700',
    sanctions:     'bg-red-100 text-red-700',
    adverse_media: 'bg-purple-100 text-purple-700',
    law_enforcement: 'bg-rose-100 text-rose-700',
  };

  const hitTypeLabels = {
    pep: 'PEP', sanctions: 'Sanctions', adverse_media: 'Adverse Media',
    law_enforcement: 'Law Enforcement', related_parties: 'Related Parties',
  };

  const statusColors = {
    pending_review: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-red-100 text-red-700',
    false_positive: 'bg-gray-100 text-gray-600',
    escalated: 'bg-orange-100 text-orange-700',
  };

  return (
    <div>
      {/* Pending Hits Alert */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-800">
            {pendingHits.length} Unresolved Screening Hit{pendingHits.length !== 1 ? 's' : ''} Require Review
          </p>
          <p className="text-xs text-amber-600 mt-0.5">These hits must be resolved before cases can progress.</p>
        </div>
        {selected.length > 0 && (
          <button className="px-3 py-1.5 bg-gray-600 text-white text-xs font-medium rounded-lg hover:bg-gray-700">
            Mark {selected.length} as False Positive
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selected.length === pendingHits.length && pendingHits.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Person</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">List</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Match %</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Case</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {screeningHits.map((hit) => (
                <tr key={hit.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    {hit.status === 'pending_review' && (
                      <input
                        type="checkbox"
                        checked={selected.includes(hit.id)}
                        onChange={() => toggleSelect(hit.id)}
                        className="rounded border-gray-300"
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{hit.person_name}</p>
                      <p className="text-xs text-gray-500">Matched: {hit.matched_name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${hitTypeColors[hit.hit_type] || 'bg-gray-100 text-gray-600'}`}>
                      {hitTypeLabels[hit.hit_type] || hit.hit_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{hit.list_name}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded ${hit.match_score >= 80 ? 'bg-red-100 text-red-700' : hit.match_score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                      {hit.match_score}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`${kycBase}/cases/${hit.case_id}`); }}
                      className="text-blue-600 hover:text-blue-700 text-xs font-medium hover:underline"
                    >
                      {hit.case_ref}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[hit.status] || 'bg-gray-100 text-gray-600'}`}>
                      {hit.status === 'pending_review' ? 'Pending' : hit.status === 'false_positive' ? 'False Positive' : hit.status.charAt(0).toUpperCase() + hit.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`${kycBase}/cases/${hit.case_id}`); }}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded-lg hover:bg-blue-100"
                    >
                      <Eye className="w-3 h-3" />
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {screeningHits.length === 0 && (
          <EmptyState icon={Shield} title="No screening hits" description="All screenings are clear. No matches found across any active cases." />
        )}
      </div>
    </div>
  );
};

// ============================================
// REVIEWS TAB
// ============================================
const ReviewsTab = ({ reviews, navigate, kycBase }) => {
  const [reviewFilter, setReviewFilter] = useState('all');
  const now = new Date();

  const filteredReviews = useMemo(() => {
    let result = [...reviews];
    if (reviewFilter === 'overdue') {
      result = result.filter(r => new Date(r.due_date) < now);
    } else if (reviewFilter === 'this_week') {
      const weekEnd = new Date(now);
      weekEnd.setDate(weekEnd.getDate() + 7);
      result = result.filter(r => {
        const d = new Date(r.due_date);
        return d >= now && d <= weekEnd;
      });
    } else if (reviewFilter === 'this_month') {
      const monthEnd = new Date(now);
      monthEnd.setDate(monthEnd.getDate() + 30);
      result = result.filter(r => {
        const d = new Date(r.due_date);
        return d >= now && d <= monthEnd;
      });
    }
    return result.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  }, [reviews, reviewFilter, now]);

  const overdueCnt = reviews.filter(r => new Date(r.due_date) < now).length;

  return (
    <div>
      {overdueCnt > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm font-semibold text-red-800">
            {overdueCnt} review{overdueCnt !== 1 ? 's' : ''} overdue and require{overdueCnt === 1 ? 's' : ''} immediate attention
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        {['all', 'overdue', 'this_week', 'this_month'].map((f) => (
          <button
            key={f}
            onClick={() => setReviewFilter(f)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${reviewFilter === f ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
          >
            {f === 'all' ? 'All' : f === 'overdue' ? 'Overdue' : f === 'this_week' ? 'Due This Week' : 'Due This Month'}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Case</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Investor</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Review Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Last Reviewed</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Risk</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredReviews.map((r, idx) => {
                const isOverdue = new Date(r.due_date) < now;
                return (
                  <tr key={idx} className={`hover:bg-gray-50 transition-colors ${isOverdue ? 'bg-red-50/50' : ''}`}>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`${kycBase}/cases/${r.case_id}`)}
                        className="text-blue-600 hover:text-blue-700 font-medium hover:underline"
                      >
                        {r.case_ref}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.investor_name}</td>
                    <td className="px-4 py-3 text-gray-600">{r.review_type}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-gray-700'}`}>
                        {new Date(r.due_date).toLocaleDateString()}
                      </span>
                      {isOverdue && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 rounded">
                          OVERDUE
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {r.last_reviewed ? new Date(r.last_reviewed).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      <KycRiskBadge rating={r.risk_rating} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => navigate(`${kycBase}/cases/${r.case_id}`)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded-lg hover:bg-blue-100"
                      >
                        Start Review
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredReviews.length === 0 && (
          <EmptyState icon={Clock} title="No reviews found" description="No reviews match the selected filter." />
        )}
      </div>
    </div>
  );
};

// ============================================
// MARKET RULES TAB
// ============================================
const MarketRulesTab = ({ marketRules }) => {
  const [expandedMarket, setExpandedMarket] = useState(null);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {marketRules.map((m) => (
          <div
            key={m.code}
            className={`bg-white rounded-xl shadow-sm border transition-all cursor-pointer ${expandedMarket === m.code ? 'border-blue-300 shadow-md' : 'border-gray-100 hover:border-gray-200 hover:shadow-md'}`}
            onClick={() => setExpandedMarket(expandedMarket === m.code ? null : m.code)}
          >
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{m.flag}</span>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{m.name}</h3>
                    <p className="text-xs text-gray-500">{m.code}</p>
                  </div>
                </div>
                <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${m.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {m.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Regulator</span>
                  <span className="text-gray-700 font-medium">{m.regulator}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">CSD</span>
                  <span className="text-gray-700 font-medium">{m.csd}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Docs Required</span>
                  <span className="text-gray-700 font-medium">{m.doc_count}</span>
                </div>
              </div>
            </div>

            {expandedMarket === m.code && (
              <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/50 rounded-b-xl">
                <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Market Details</h4>
                <p className="text-xs text-gray-600 mb-2">
                  Regulated by {m.regulator}. Securities held at {m.csd}.
                  {m.doc_count} document types required for full onboarding.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Globe className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs text-gray-500">Country risk data available via Risk Scoring engine</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// REPORTING TAB
// ============================================
const ReportingTab = ({ cases, screeningHits, stats }) => {
  const statusCounts = useMemo(() => {
    const counts = {};
    cases.forEach(c => { counts[c.status] = (counts[c.status] || 0) + 1; });
    return Object.entries(counts).map(([status, count]) => ({ status, count }));
  }, [cases]);

  const riskCounts = useMemo(() => {
    const counts = {};
    cases.forEach(c => {
      if (c.risk_rating) counts[c.risk_rating] = (counts[c.risk_rating] || 0) + 1;
    });
    return counts;
  }, [cases]);

  const marketCoverage = useMemo(() => {
    const mktSet = new Set();
    cases.forEach(c => c.markets.forEach(m => mktSet.add(m)));
    return mktSet.size;
  }, [cases]);

  const reports = [
    {
      title: 'Cases by Status',
      icon: FileText,
      color: 'blue',
      metrics: statusCounts.map(s => `${s.status.replace(/_/g, ' ')}: ${s.count}`).join(' | '),
    },
    {
      title: 'Cases by Risk',
      icon: AlertTriangle,
      color: 'amber',
      metrics: `Low: ${riskCounts.low || 0} | Medium: ${riskCounts.medium || 0} | High: ${riskCounts.high || 0} | Very High: ${riskCounts.very_high || 0}`,
    },
    {
      title: 'SLA Performance',
      icon: Clock,
      color: 'green',
      metrics: `On Track: ${stats.total - stats.overdueSla} | Overdue: ${stats.overdueSla} | Compliance Rate: ${((1 - stats.overdueSla / Math.max(stats.total, 1)) * 100).toFixed(0)}%`,
    },
    {
      title: 'Screening Summary',
      icon: Shield,
      color: 'purple',
      metrics: `Total Hits: ${screeningHits.length} | Pending: ${stats.pendingHits} | Confirmed: ${screeningHits.filter(h => h.status === 'confirmed').length}`,
    },
    {
      title: 'Market Coverage',
      icon: Globe,
      color: 'indigo',
      metrics: `Active Markets: ${marketCoverage} | Total Cases: ${stats.total} | Upcoming Reviews: ${stats.upcomingReviews}`,
    },
  ];

  const iconColors = {
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    indigo: 'bg-indigo-100 text-indigo-600',
  };

  return (
    <div className="space-y-4">
      {reports.map((r, idx) => (
        <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${iconColors[r.color]}`}>
              <r.icon className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">{r.title}</h3>
              <p className="text-xs text-gray-500 mb-3">{r.metrics}</p>
              <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors">
                <Download className="w-3.5 h-3.5" />
                Generate Report
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================
// MAIN DASHBOARD
// ============================================
const KycDashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = getActiveTab(location.pathname);

  // Derive base path for KYC links so the same component works under any role prefix
  const kycBase = location.pathname.replace(/\/kyc.*$/, '/kyc');
  const { loading, stats, cases, screeningHits, reviews, marketRules } = useKycMockData();
  const userRole = 'bank_compliance_maker'; // Default role for now

  const roleLabels = {
    bank_compliance_maker: 'Compliance Maker',
    bank_compliance_checker: 'Compliance Checker',
    platform_admin: 'Platform Admin',
    bank_admin: 'Relationship Manager',
    investor_family_office: 'Family Office',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <span>Dashboard</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-900 font-medium">KYC &amp; Onboarding</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">KYC &amp; Onboarding</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700`}>
              <Shield className="w-3.5 h-3.5" />
              {roleLabels[userRole] || userRole}
            </span>
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Refresh">
              <RefreshCw className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          <StatCard title="Total Cases" value={stats.total} icon={FileText} color="blue" subtitle="All active cases" />
          <StatCard title="In Review" value={stats.inReview} icon={Eye} color="yellow" subtitle="Requires attention" />
          <StatCard
            title="Pending Hits"
            value={stats.pendingHits}
            icon={AlertTriangle}
            color={stats.pendingHits > 0 ? 'red' : 'green'}
            subtitle="Screening matches"
            onClick={() => navigate(`${kycBase}/screening`)}
          />
          <StatCard
            title="Overdue SLA"
            value={stats.overdueSla}
            icon={Clock}
            color={stats.overdueSla > 0 ? 'red' : 'green'}
            subtitle="Past deadline"
          />
          <StatCard
            title="Upcoming Reviews"
            value={stats.upcomingReviews}
            icon={Calendar}
            color="purple"
            subtitle="Next 30 days"
            onClick={() => navigate(`${kycBase}/reviews`)}
          />
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-1 -mb-px overflow-x-auto">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => navigate(`${kycBase}${tab.suffix}`)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'queue' && <CaseQueueTab cases={cases} navigate={navigate} kycBase={kycBase} />}
        {activeTab === 'screening' && <ScreeningCentreTab screeningHits={screeningHits} navigate={navigate} kycBase={kycBase} />}
        {activeTab === 'reviews' && <ReviewsTab reviews={reviews} navigate={navigate} kycBase={kycBase} />}
        {activeTab === 'markets' && <MarketRulesTab marketRules={marketRules} />}
        {activeTab === 'reporting' && <ReportingTab cases={cases} screeningHits={screeningHits} stats={stats} />}
      </div>
    </div>
  );
};

export default KycDashboardPage;
